import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, Facebook, Instagram, Loader2, Phone, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { socialLinks } from '../mock';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { language, t } = useLanguage();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const successMessages = {
    it: {
      title: 'Richiesta inviata!',
      description: 'Ti risponderemo al più presto.',
    },
    en: {
      title: 'Request sent!',
      description: 'We will get back to you as soon as possible.',
    },
    de: {
      title: 'Anfrage gesendet!',
      description: 'Wir werden uns so schnell wie möglich bei Ihnen melden.',
    },
    fr: {
      title: 'Demande envoyée!',
      description: 'Nous vous répondrons dès que possible.',
    },
    es: {
      title: '¡Solicitud enviada!',
      description: 'Te responderemos lo antes posible.',
    },
  };

  const errorMessages = {
    it: {
      title: 'Errore',
      description: 'Si è verificato un errore. Riprova più tardi.',
    },
    en: {
      title: 'Error',
      description: 'An error occurred. Please try again later.',
    },
    de: {
      title: 'Fehler',
      description: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
    },
    fr: {
      title: 'Erreur',
      description: 'Une erreur est survenue. Veuillez réessayer plus tard.',
    },
    es: {
      title: 'Error',
      description: 'Ha ocurrido un error. Por favor, inténtalo de nuevo más tarde.',
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/`, {
        ...formData,
        language,
      });

      toast({
        title: successMessages[language].title,
        description: successMessages[language].description,
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast({
        title: errorMessages[language].title,
        description: errorMessages[language].description,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contatti" ref={ref} className="relative py-32 bg-[#2A2520]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-9xl font-serif text-[#F5F1E8]/10 absolute top-10 left-6"
        >
          05
        </motion.div>

        <div className="relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-serif text-[#F5F1E8] mb-4">
              {t.contact.title}
            </h2>
            <p className="text-xl text-[#D4AF37] tracking-wider uppercase">
              {t.contact.subtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-[#F5F1E8] mb-2 text-sm tracking-wider uppercase"
                  >
                    {t.contact.form.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="w-full bg-[#F5F1E8]/5 border border-[#F5F1E8]/20 px-4 py-3 text-[#F5F1E8] focus:outline-none focus:border-[#D4AF37] transition-colors duration-300"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-[#F5F1E8] mb-2 text-sm tracking-wider uppercase"
                  >
                    {t.contact.form.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="w-full bg-[#F5F1E8]/5 border border-[#F5F1E8]/20 px-4 py-3 text-[#F5F1E8] focus:outline-none focus:border-[#D4AF37] transition-colors duration-300"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-[#F5F1E8] mb-2 text-sm tracking-wider uppercase"
                  >
                    {t.contact.form.phone}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="contact-phone-input"
                    className="w-full bg-[#F5F1E8]/5 border border-[#F5F1E8]/20 px-4 py-3 text-[#F5F1E8] focus:outline-none focus:border-[#D4AF37] transition-colors duration-300"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-[#F5F1E8] mb-2 text-sm tracking-wider uppercase"
                  >
                    {t.contact.form.message}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    data-testid="contact-message-input"
                    className="w-full bg-[#F5F1E8]/5 border border-[#F5F1E8]/20 px-4 py-3 text-[#F5F1E8] focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 resize-none"
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  className="w-full bg-[#C9826B] text-[#F5F1E8] py-4 px-6 rounded-none tracking-widest uppercase text-sm font-semibold hover:bg-[#D4AF37] transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-[#D4AF37]/50 disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="submit-contact-btn"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  {t.contact.form.submit}
                </motion.button>
              </form>
            </motion.div>

            {/* Social & Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="flex flex-col justify-center space-y-6"
            >
              {/* Phone */}
              <motion.a
                href={socialLinks.phoneLink}
                whileHover={{ scale: 1.02 }}
                className="group block bg-[#F5F1E8]/5 backdrop-blur-sm border border-[#F5F1E8]/10 hover:border-[#D4AF37]/50 p-6 transition-all duration-300"
                data-testid="contact-phone-link"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#D4AF37]/20 flex items-center justify-center group-hover:bg-[#D4AF37]/30 transition-colors duration-300">
                    <Phone className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#F5F1E8]/60 text-xs tracking-widest uppercase mb-1">
                      {t.contact.phoneLabel}
                    </p>
                    <p className="text-[#F5F1E8] text-xl font-light tracking-wide">
                      {socialLinks.phone}
                    </p>
                  </div>
                </div>
              </motion.a>

              {/* WhatsApp */}
              <motion.a
                href={socialLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                className="group block bg-[#25D366]/10 backdrop-blur-sm border border-[#25D366]/30 hover:border-[#25D366] p-6 transition-all duration-300"
                data-testid="contact-whatsapp-link"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#25D366]/20 flex items-center justify-center group-hover:bg-[#25D366]/30 transition-colors duration-300">
                    <MessageCircle className="w-6 h-6 text-[#25D366]" />
                  </div>
                  <div>
                    <p className="text-[#25D366] text-xs tracking-widest uppercase mb-1 font-semibold">
                      WhatsApp
                    </p>
                    <p className="text-[#F5F1E8] text-base">
                      {t.contact.whatsappCta}
                    </p>
                  </div>
                </div>
              </motion.a>

              {/* Social */}
              <div>
                <h3 className="text-lg font-serif text-[#F5F1E8] mb-4">
                  {t.contact.social}
                </h3>
                <div className="flex gap-4">
                  <motion.a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -5 }}
                    className="w-12 h-12 rounded-full bg-[#F5F1E8]/10 border border-[#F5F1E8]/20 flex items-center justify-center hover:bg-[#D4AF37]/20 hover:border-[#D4AF37] transition-all duration-300"
                    data-testid="social-facebook-link"
                  >
                    <Facebook className="w-5 h-5 text-[#F5F1E8]" />
                  </motion.a>
                  <motion.a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -5 }}
                    className="w-12 h-12 rounded-full bg-[#F5F1E8]/10 border border-[#F5F1E8]/20 flex items-center justify-center hover:bg-[#D4AF37]/20 hover:border-[#D4AF37] transition-all duration-300"
                    data-testid="social-instagram-link"
                  >
                    <Instagram className="w-5 h-5 text-[#F5F1E8]" />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
    </section>
  );
};

export default ContactSection;
